import React, { useState } from 'react';
import { MessageSquarePlus, ChevronDown, ChevronUp, Clock, CheckCircle2, Wrench, XCircle, Eye, Send, Plus, Trash2 } from 'lucide-react';
import StatusBanner from '../components/StatusBanner';
import { usePermissions } from '../hooks/usePermissions';
import {
  useGetSupportTicketsQuery,
  useCreateSupportTicketMutation,
  useAddTicketUpdateMutation,
  useDeleteTicketMutation,
  type SupportTicket,
  type TicketStatus,
  type TicketType,
} from '../services/supportTicketApi';

// ── helpers ─────────────────────────────────────────────────────────────────

const STATUS_META: Record<TicketStatus, { label: string; color: string; icon: React.ReactNode }> = {
  open:         { label: 'Open',         color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',   icon: <Clock className="h-3 w-3" /> },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', icon: <Eye className="h-3 w-3" /> },
  in_progress:  { label: 'In Progress',  color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300', icon: <Wrench className="h-3 w-3" /> },
  completed:    { label: 'Completed',    color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',  icon: <CheckCircle2 className="h-3 w-3" /> },
  wont_fix:     { label: "Won't Fix",    color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',        icon: <XCircle className="h-3 w-3" /> },
};

const TYPE_LABELS: Record<TicketType, string> = {
  bug:             '🐛 Bug Report',
  feature_request: '✨ Feature Request',
  other:           '💬 Other',
};

function StatusBadge({ status }: { status: TicketStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.color}`}>
      {m.icon}{m.label}
    </span>
  );
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── sub-components ───────────────────────────────────────────────────────────

function TicketCard({ ticket, isAdmin }: { ticket: SupportTicket; isAdmin: boolean }) {
  const [open, setOpen]       = useState(false);
  const [reply, setReply]     = useState('');
  const [newStatus, setNewStatus] = useState<TicketStatus | ''>('');
  const [addUpdate, { isLoading }] = useAddTicketUpdateMutation();
  const [deleteTicket, { isLoading: isDeleting }] = useDeleteTicketMutation();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ticket #${ticket.id}: "${ticket.title}"? This cannot be undone.`)) return;
    await deleteTicket(ticket.id);
  };

  const handleSubmit = async () => {
    if (!reply.trim()) return;
    await addUpdate({ id: ticket.id, message: reply.trim(), ...(newStatus ? { new_status: newStatus as TicketStatus } : {}) });
    setReply('');
    setNewStatus('');
  };

  const isClosed = ticket.status === 'completed' || ticket.status === 'wont_fix';

  return (
    <div className="bg-app-panel dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* header row */}
      <button
        className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs text-gray-400 dark:text-gray-500">#{ticket.id}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{TYPE_LABELS[ticket.type]}</span>
            <StatusBadge status={ticket.status} />
            {isAdmin && (
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                {ticket.user_name} · {ticket.company_name}
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ticket.title}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Raised {timeAgo(ticket.created_at)}
            {ticket.updates.length > 0 && ` · ${ticket.updates.length} update${ticket.updates.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />}
      </button>
      {isAdmin && (
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="px-3 py-3 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-40 border-l border-gray-200 dark:border-gray-700 flex-shrink-0"
          title="Delete ticket"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 space-y-4">
          {/* original description */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{ticket.user_name} · {new Date(ticket.created_at).toLocaleString('en-GB')}</p>
            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* updates thread */}
          {ticket.updates.map(u => (
            <div key={u.id} className={`rounded-md p-3 ${u.is_admin ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800' : 'bg-gray-50 dark:bg-gray-900/50'}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{u.author_name}</span>
                {u.is_admin && <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-full font-medium">Support</span>}
                <span className="text-xs text-gray-400 dark:text-gray-500">{timeAgo(u.created_at)}</span>
                {u.new_status && (
                  <span className="ml-auto"><StatusBadge status={u.new_status} /></span>
                )}
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{u.message}</p>
            </div>
          ))}

          {/* reply box — admins always, users only if not closed */}
          {(isAdmin || !isClosed) && (
            <div className="space-y-2">
              <textarea
                rows={3}
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder={isAdmin ? 'Add a status update or reply…' : 'Add a comment or provide more details…'}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              />
              <div className="flex items-center gap-2 flex-wrap">
                {isAdmin && (
                  <select
                    value={newStatus}
                    onChange={e => setNewStatus(e.target.value as TicketStatus | '')}
                    className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">Keep current status</option>
                    {(Object.keys(STATUS_META) as TicketStatus[]).map(s => (
                      <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !reply.trim()}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="h-3.5 w-3.5" />
                  {isLoading ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          )}
          {isClosed && !isAdmin && (
            <p className="text-xs text-center text-gray-400 dark:text-gray-500 italic">This ticket is closed.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── main page ────────────────────────────────────────────────────────────────

const ACTIVE_STATUSES: TicketStatus[] = ['open', 'under_review', 'in_progress'];
const CLOSED_STATUSES: TicketStatus[] = ['completed', 'wont_fix'];

export default function SupportPage() {
  const { canManageUsers } = usePermissions();
  const isAdmin = canManageUsers;

  const { data: tickets = [], isLoading } = useGetSupportTicketsQuery(undefined, {
    pollingInterval: 30000,
  });
  const [createTicket, { isLoading: creating }] = useCreateSupportTicketMutation();

  const [showForm, setShowForm]         = useState(false);
  const [formType, setFormType]         = useState<TicketType>('bug');
  const [formTitle, setFormTitle]       = useState('');
  const [formDesc, setFormDesc]         = useState('');
  const [formError, setFormError]       = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formTitle.trim() || !formDesc.trim()) {
      setFormError('Please fill in all fields.');
      return;
    }
    try {
      await createTicket({ type: formType, title: formTitle.trim(), description: formDesc.trim() }).unwrap();
      setFormTitle('');
      setFormDesc('');
      setFormType('bug');
      setShowForm(false);
    } catch {
      setFormError('Failed to submit. Please try again.');
    }
  };

  const activeTickets    = tickets.filter(t => ACTIVE_STATUSES.includes(t.status));
  const completedTickets = tickets.filter(t => CLOSED_STATUSES.includes(t.status));
  const openCount        = activeTickets.length;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageSquarePlus className="h-6 w-6 text-indigo-500" />
              {isAdmin ? 'Support Tickets' : 'Report an Issue'}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isAdmin
                ? `${openCount} active ticket${openCount !== 1 ? 's' : ''} across your organisation`
                : 'Report a bug or request a new feature. We\'ll keep you updated on progress.'}
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              New Ticket
            </button>
          )}
        </div>
      </div>

      <StatusBanner />

      {/* new ticket form */}
      {showForm && (
        <div className="mb-6 bg-app-panel dark:bg-gray-800 rounded-lg border border-indigo-200 dark:border-indigo-800 p-4 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Submit a New Ticket</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <div className="flex flex-wrap gap-2">
                {(['bug', 'feature_request', 'other'] as TicketType[]).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormType(t)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      formType === t
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400'
                    }`}
                  >
                    {TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                placeholder="Brief summary of the issue or request"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                rows={5}
                value={formDesc}
                onChange={e => setFormDesc(e.target.value)}
                placeholder={formType === 'bug'
                  ? 'What happened? What did you expect? Steps to reproduce…'
                  : formType === 'feature_request'
                  ? 'Describe the feature and how it would help you…'
                  : 'Provide as much detail as possible…'}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              />
            </div>
            {formError && <p className="text-sm text-red-500 dark:text-red-400">{formError}</p>}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormError(null); }}
                className="px-4 py-2 text-sm rounded-md bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Submitting…' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* active ticket list */}
      {isLoading ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-12">Loading tickets…</p>
      ) : activeTickets.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquarePlus className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">No active tickets</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Use the "New Ticket" button above to report an issue or request a feature.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeTickets.map(t => (
            <TicketCard key={t.id} ticket={t} isAdmin={isAdmin} />
          ))}
        </div>
      )}

      {/* completed / won't fix section */}
      {completedTickets.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowCompleted(v => !v)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            {showCompleted ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Completed &amp; Closed ({completedTickets.length})
          </button>
          {showCompleted && (
            <div className="space-y-3 mt-3">
              {completedTickets.map(t => (
                <TicketCard key={t.id} ticket={t} isAdmin={isAdmin} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
